// NOM-035 — Funciones de calificación (puras, sin dependencias de React)
// Extraídas del componente monolítico para uso compartido client/server

import { GUIA_II_PREGUNTAS, ITEMS_INVERTIDOS_GII } from '../data/guia-ii';
import { GUIA_III_PREGUNTAS, ITEMS_INVERTIDOS_GIII } from '../data/guia-iii';
import {
  NIVELES_RIESGO_GII, NIVELES_CATEGORIA_GII, NIVELES_DOMINIO_GII,
  NIVELES_RIESGO_GIII, NIVELES_CATEGORIA_GIII, NIVELES_DOMINIO_GIII,
} from '../data/baremos';

// ── Helpers ────────────────────────────────────────────────────

export function determinarNivel(puntaje, tablaRangos) {
  if (!tablaRangos) return "Nulo";
  for (const rango of tablaRangos) {
    if (puntaje >= rango.min && puntaje <= rango.max) return rango.nivel;
  }
  return "Nulo";
}

/**
 * Califica un cuestionario Likert genérico (GII o GIII).
 * Recibe la estructura de preguntas, ítems invertidos y tablas de niveles.
 */
function calificarLikert(respuestas, preguntasData, itemsInvertidos, nivelesRiesgo, nivelesCat, nivelesDom) {
  const dominios = {};
  const categorias = {};
  let totalFinal = 0;

  preguntasData.forEach(seccion => {
    let sumaDominio = 0;
    seccion.preguntas.forEach(p => {
      const resp = respuestas[p.id];
      if (resp !== undefined) {
        const invertido = itemsInvertidos.includes(p.id);
        const puntaje = invertido ? resp : (4 - resp);
        sumaDominio += puntaje;
      }
    });
    totalFinal += sumaDominio;

    // Acumular por dominio (puede repetirse, ej. "Falta de control")
    if (!dominios[seccion.dominio]) {
      dominios[seccion.dominio] = { puntaje: 0, categoria: seccion.categoria };
    }
    dominios[seccion.dominio].puntaje += sumaDominio;

    // Acumular por categoría
    if (!categorias[seccion.categoria]) {
      categorias[seccion.categoria] = { puntaje: 0 };
    }
    categorias[seccion.categoria].puntaje += sumaDominio;
  });

  // Asignar niveles
  Object.keys(dominios).forEach(dom => {
    dominios[dom].nivel = determinarNivel(dominios[dom].puntaje, nivelesDom[dom]);
  });
  Object.keys(categorias).forEach(cat => {
    categorias[cat].nivel = determinarNivel(categorias[cat].puntaje, nivelesCat[cat]);
  });
  const nivelFinal = determinarNivel(totalFinal, nivelesRiesgo);

  return { totalFinal, nivelFinal, dominios, categorias };
}

// ── API pública ────────────────────────────────────────────────

/**
 * Califica Guía I: Acontecimientos Traumáticos Severos
 * @param {Object} respuestas - { g1_1: true/false, g1_2: true/false, ... }
 * @returns {{ acontecimientos, recuerdos, evitacion, afectacion, requiereAtencion }}
 */
export function calificarGuiaI(respuestas) {
  const acontecimientos = ["g1_1","g1_2","g1_3","g1_4","g1_5","g1_6"].filter(id => respuestas[id] === true);
  const recuerdos = ["g1_7","g1_8"].filter(id => respuestas[id] === true);
  const evitacion = ["g1_9","g1_10","g1_11","g1_12","g1_13","g1_14","g1_15"].filter(id => respuestas[id] === true);
  const afectacion = ["g1_16","g1_17","g1_18","g1_19","g1_20"].filter(id => respuestas[id] === true);

  const requiereAtencion = acontecimientos.length > 0 &&
    (recuerdos.length >= 1 || evitacion.length >= 3 || afectacion.length >= 2);

  return {
    acontecimientos: acontecimientos.length,
    recuerdos: recuerdos.length,
    evitacion: evitacion.length,
    afectacion: afectacion.length,
    requiereAtencion,
  };
}

/**
 * Califica Guía II: Factores de Riesgo Psicosocial (16–50 trabajadores)
 * @param {Object} respuestas - { g2_1: 0-4, g2_2: 0-4, ... }
 * @returns {{ totalFinal, nivelFinal, dominios, categorias }}
 */
export function calificarGuiaII(respuestas) {
  return calificarLikert(
    respuestas, GUIA_II_PREGUNTAS, ITEMS_INVERTIDOS_GII,
    NIVELES_RIESGO_GII, NIVELES_CATEGORIA_GII, NIVELES_DOMINIO_GII
  );
}

/**
 * Califica Guía III: Factores de Riesgo + Entorno Organizacional (51+ trabajadores)
 * @param {Object} respuestas - { g3_1: 0-4, g3_2: 0-4, ... }
 * @returns {{ totalFinal, nivelFinal, dominios, categorias }}
 */
export function calificarGuiaIII(respuestas) {
  return calificarLikert(
    respuestas, GUIA_III_PREGUNTAS, ITEMS_INVERTIDOS_GIII,
    NIVELES_RIESGO_GIII, NIVELES_CATEGORIA_GIII, NIVELES_DOMINIO_GIII
  );
}

/**
 * Calcula tamaño de muestra para Guía III (Ecuación 1, NOM-035 III.1)
 * n = 0.9604*N / (0.0025*(N-1) + 0.9604)
 */
export function calcularMuestraGIII(N) {
  if (N <= 50) return null;
  return Math.ceil((0.9604 * N) / (0.0025 * (N - 1) + 0.9604));
}

/**
 * Determina obligaciones según tamaño del centro
 */
export function determinarObligaciones(numTrabajadores) {
  const n = numTrabajadores;
  if (n <= 15) return { nivel: 1, guias: ["I"], label: "1-15 trabajadores", desc: "Política + Guía I" };
  if (n <= 50) return { nivel: 2, guias: ["I", "II"], label: "16-50 trabajadores", desc: "Política + Guía I + Guía II" };
  return { nivel: 3, guias: ["I", "III"], label: "51+ trabajadores", desc: "Política + Guía I + Guía III" };
}
